// src/pages/MainPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import "./Dashboard.css"; 
import { useAuth } from "../context/AuthContext"; 
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const WORKING_HOURS_YEAR = 1920; // 매뉴얼 기준 연간 총 근무시간  [oai_citation:2‡인재경영 업무현황 조사.pptx](sediment://file_000000009368720980e7a4cb17b69a3b)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL || "";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (SUPABASE_URL ? `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1` : "http://localhost:4000/api");
const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "";
const DEFAULT_TASK_ROWS = 5;
const MAX_TASK_ROWS = 40;

function createEmptyTaskRow(id) {
  return {
    id,
    category: "",
    taskName: "",
    frequency: "연간",
    hours: "",
  };
}

function convertToAnnualHours(task) {
  const h = Number(task.hours);
  if (!Number.isFinite(h) || h <= 0) return 0;

  switch (task.frequency) {
    case "일간":
      // 일간: 한 달 20일 기준, 12개월
      return h * 20 * 12;
    case "주간":
      // 주간: 한 달 3주 기준, 12개월
      return h * 3 * 12;
    case "월간":
      // 월간: 1년 12개월
      return h * 12;
    case "분기":
      // 분기: 1년 4분기
      return h * 4;
    case "반기":
      // 반기: 1년 2번
      return h * 2;
    case "연간":
    default:
      // 이미 연간 기준 입력으로 처리
      return h;
  }
}

function MainPage() {
  const { user, token, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const getAccessToken = async () => {
    if (token) return token;
    if (!supabase) return null;
    let { data } = await supabase.auth.getSession();
    let accessToken = data?.session?.access_token || null;
    // 로그인 직후 리다이렉트 시 세션 반영 지연 대응: 한 번 더 시도
    if (!accessToken) {
      await new Promise((r) => setTimeout(r, 400));
      const retry = await supabase.auth.getSession();
      accessToken = retry?.data?.session?.access_token || null;
    }
    return accessToken;
  };

  // 401 시 새 세션으로 1회 재시도 후 실패 시에만 로그아웃 (로그인 직후 자동 로그아웃 방지)
  const fetchWithAuthRetry = async (url, options = {}, retried = false) => {
    let accessToken = await getAccessToken();
    if (!accessToken) return null;
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON_KEY,
        ...options.headers,
      },
    });
    if (res.status === 401 && !retried && supabase) {
      const { data } = await supabase.auth.getSession();
      const newToken = data?.session?.access_token;
      if (newToken) {
        const retryRes = await fetch(url, {
          ...options,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${newToken}`,
            apikey: SUPABASE_ANON_KEY,
            ...options.headers,
          },
        });
        if (retryRes.status === 401) {
          alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
          logout();
          navigate("/login", { replace: true });
          return null;
        }
        return retryRes;
      }
    }
    if (res.status === 401) {
      alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
      logout();
      navigate("/login", { replace: true });
      return null;
    }
    return res;
  };

  const [jobTasks, setJobTasks] = useState([]); // 관리자 페이지에서 관리하는 업무분장표
  const [jobTasksLoading, setJobTasksLoading] = useState(true);
  const [jobTasksError, setJobTasksError] = useState("");

  const [, setInitialLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [profileForm, setProfileForm] = useState({
    department: "",
    duty: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  const [hraRatio, setHraRatio] = useState("");
  const [hraRatioError, setHraRatioError] = useState("");

  const [tasks, setTasks] = useState(
    Array.from({ length: DEFAULT_TASK_ROWS }, (_, idx) =>
      createEmptyTaskRow(Date.now() + idx)
    )
  );
  const [taskStatus, setTaskStatus] = useState("draft");

  useEffect(() => {
    const fetchJobTasks = async () => {
      try {
        setJobTasksLoading(true);
        const res = await fetchWithAuthRetry(`${API_BASE_URL}/job-tasks`, { method: "GET" });
        if (!res) return; // 이미 로그아웃 처리됨

        if (!res.ok) {
          throw new Error("업무분장표를 불러오지 못했습니다.");
        }

        const data = await res.json();
        const normalized = (Array.isArray(data) ? data : []).map((jt) => ({
          id: jt.id,
          category: jt.category_name || "",
          taskName: jt.activity_desc || "",
        }));
        setJobTasks(normalized);
        setJobTasksError("");
      } catch (err) {
        console.error("jobTasks fetch error", err);
        setJobTasksError(
          err?.message || "업무분장표를 불러오는 중 오류가 발생했습니다."
        );
      } finally {
        setJobTasksLoading(false);
      }
    };

    fetchJobTasks();
  }, [token, logout, navigate]);

  useEffect(() => {
    const fetchUserTasks = async () => {
      if (!token) {
        setInitialLoading(false);
        return;
      }
      try {
        setInitialLoading(true);
        const res = await fetchWithAuthRetry(`${API_BASE_URL}/user-tasks`, { method: "GET" });
        if (!res) {
          setInitialLoading(false);
          return; // 이미 로그아웃 처리됨
        }

        if (!res.ok) {
          throw new Error("저장된 업무 현황을 불러오지 못했습니다.");
        }

        const data = await res.json();

        // hraRatio 설정
        if (data.hraRatio !== null && data.hraRatio !== undefined) {
          setHraRatio(String(data.hraRatio));
        }

        if (data?.status) {
          setTaskStatus(data.status);
        } else {
          setTaskStatus("draft");
        }

        // data는 사용자별 수행업무 배열이거나 객체의 tasks 속성일 수 있음
        const tasksData = Array.isArray(data) ? data : (data.tasks || []);
        if (Array.isArray(tasksData) && tasksData.length > 0) {
          const mapped = tasksData.map((t, index) => ({
            id: t.id ?? Date.now() + index,
            category: t.category || "",
            taskName: t.task_name || t.taskName || "",
            frequency: t.frequency || "연간",
            hours:
              t.hours === null || t.hours === undefined
                ? ""
                : String(t.hours),
          }));
          const padded =
            mapped.length >= DEFAULT_TASK_ROWS
              ? mapped
              : [
                  ...mapped,
                  ...Array.from(
                    { length: DEFAULT_TASK_ROWS - mapped.length },
                    (_, idx) => createEmptyTaskRow(Date.now() + idx)
                  ),
                ];
          setTasks(padded);
        } else {
          setTasks(
            Array.from({ length: DEFAULT_TASK_ROWS }, (_, idx) =>
              createEmptyTaskRow(Date.now() + idx)
            )
          );
        }
      } catch (err) {
        console.error("user-tasks fetch error", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserTasks();
  }, [token]);

  const profile = {
    name: user?.name ?? "",
    department: user?.department ?? "",
    duty: user?.duty ?? "",
  };

  useEffect(() => {
    setProfileForm({
      department: user?.department ?? "",
      duty: user?.duty ?? "",
    });
  }, [user?.department, user?.duty]);

  const handleLogout = () => {
    logout();
    navigate("/"); // 로그인 페이지 경로에 맞게 조정 가능
  };

  const handleProfileChange = (field, value) => {
    setProfileForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setProfileError("");
    setProfileSuccess("");
  };

  const handleProfileSave = async () => {
    try {
      setProfileSaving(true);
      setProfileError("");
      setProfileSuccess("");

      await updateProfile({
        department: profileForm.department,
        duty: profileForm.duty,
      });

      setProfileSuccess("인적 정보가 저장되었습니다.");
    } catch (err) {
      console.error("profile update error", err);
      setProfileError("인적 정보 저장 중 오류가 발생했습니다.");
    } finally {
      setProfileSaving(false);
    }
  };

  const categories = useMemo(
    () => Array.from(new Set(jobTasks.map((jt) => jt.category).filter(Boolean))),
    [jobTasks]
  );

  // 총 투입 시간 & 수행비율 (%) 계산
  const { totalHours, ratio } = useMemo(() => {
    const total = tasks.reduce((sum, t) => {
      return sum + convertToAnnualHours(t);
    }, 0);

    const r = WORKING_HOURS_YEAR > 0 ? (total / WORKING_HOURS_YEAR) * 100 : 0;
    return {
      totalHours: total,
      ratio: Number.isFinite(r) ? Math.round(r * 10) / 10 : 0,
    };
  }, [tasks]);

  const handleHraRatioChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setHraRatio("");
      setHraRatioError("");
      return;
    }

    const num = Number(value);
    if (!Number.isFinite(num)) {
      setHraRatioError("숫자를 입력해 주세요.");
    } else if (num < 0) {
      setHraRatioError("0 이상 값을 입력해 주세요.");
    } else if (num > 100) {
      setHraRatioError("100을 넘으면 에러입니다. 다시 입력해 주세요.");
    } else {
      setHraRatioError("");
    }
    setHraRatio(value);
  };

  const handleTaskChange = (id, field, value) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const addTaskRow = () => {
    if (taskStatus === "submitted") {
      alert("최종 제출 이후에는 수정할 수 없습니다.");
      return;
    }
    if (tasks.length >= MAX_TASK_ROWS) {
      alert(`수행 업무는 최대 ${MAX_TASK_ROWS}개까지 입력할 수 있습니다.`);
      return;
    }
    setTasks((prev) => [
      ...prev,
      createEmptyTaskRow(Date.now()),
    ]);
  };

  const removeTaskRow = (id) => {
    if (taskStatus === "submitted") {
      alert("최종 제출 이후에는 수정할 수 없습니다.");
      return;
    }
    setTasks((prev) => {
      const filtered = prev.filter((t) => t.id !== id);
      // 최소 1개는 유지
      return filtered.length > 0 ? filtered : prev;
    });
  };

  const handleSubmit = async () => {
    if (taskStatus === "submitted") {
      alert("최종 제출 이후에는 수정할 수 없습니다.");
      return;
    }
    if (hraRatioError) {
      alert("인재경영 업무 비중 입력 오류를 먼저 수정해 주세요.");
      return;
    }

    try {
      setSaveLoading(true);
      setSaveError("");
      setSaveSuccess("");

      const payload = {
        hraRatio:
          hraRatio === null || hraRatio === undefined || hraRatio === ""
            ? null
            : Number(hraRatio),
        tasks: tasks.map((t, index) => ({
          jobTaskId: null, // 필요하면 나중에 실제 job_tasks.id와 매핑
          category: t.category || null,
          taskName: t.taskName || null,
          frequency: t.frequency || null,
          hours:
            t.hours === null || t.hours === undefined || t.hours === ""
              ? null
              : Number(t.hours),
          annualHours: convertToAnnualHours(t),
          sortOrder: index + 1,
        })),
      };

      const accessToken = await getAccessToken();
      if (!accessToken) {
        alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      const res = await fetch(`${API_BASE_URL}/user-tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = `저장 중 오류가 발생했습니다. (status ${res.status})`;
        setSaveError(msg);
        alert(msg);
        return;
      }

      setSaveSuccess("저장되었습니다.");
      alert("저장되었습니다.");
    } catch (err) {
      console.error("save workload error", err);
      setSaveError("저장 중 오류가 발생했습니다.");
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (taskStatus === "submitted") {
      alert("이미 최종 제출된 상태입니다.");
      return;
    }
    if (hraRatioError) {
      alert("인재경영 업무 비중 입력 오류를 먼저 수정해 주세요.");
      return;
    }
    const ok = window.confirm(
      "최종 제출 후에는 수정이 불가능합니다. 제출하시겠습니까?"
    );
    if (!ok) return;

    try {
      setSaveLoading(true);
      setSaveError("");
      setSaveSuccess("");

      const payload = {
        hraRatio:
          hraRatio === null || hraRatio === undefined || hraRatio === ""
            ? null
            : Number(hraRatio),
        tasks: tasks.map((t, index) => ({
          jobTaskId: null,
          category: t.category || null,
          taskName: t.taskName || null,
          frequency: t.frequency || null,
          hours:
            t.hours === null || t.hours === undefined || t.hours === ""
              ? null
              : Number(t.hours),
          annualHours: convertToAnnualHours(t),
          sortOrder: index + 1,
        })),
      };

      const accessToken = await getAccessToken();
      if (!accessToken) {
        alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      const res = await fetch(`${API_BASE_URL}/user-tasks-submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = `최종 제출 중 오류가 발생했습니다. (status ${res.status})`;
        setSaveError(msg);
        alert(msg);
        return;
      }

      setTaskStatus("submitted");
      setSaveSuccess("최종 제출되었습니다.");
      alert("최종 제출되었습니다.");
    } catch (err) {
      console.error("final submit error", err);
      setSaveError("최종 제출 중 오류가 발생했습니다.");
      alert("최종 제출 중 오류가 발생했습니다.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* 페이지 타이틀 */}
        <header className="dashboard-header">
          <div>
            <h1 className="dashboard-title">주요 핵심 업무 활동 수행시간 조사</h1>
            <p className="dashboard-subtitle">
              1년을 기준으로 주요 핵심 업무 활동 -Key activity- 수행시간을 입력해 주세요.
            </p>
          </div>
          <div className="dashboard-header-right">
            <div className="dashboard-user-badge">
              <span className="user-name">{profile.name}</span>
              <span className="user-dept">{profile.department}</span>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleLogout}
              style={{ marginLeft: "12px" }}
            >
              로그아웃
            </button>
          </div>
        </header>

        {/* 상단 인적 정보 카드 */}
        <section className="panel">
          <h2 className="panel-title">인적 정보</h2>
          <div className="profile-grid">
            <div className="profile-item">
              <span className="label">이름</span>
              <span className="value">{profile.name}</span>
            </div>
            <div className="profile-item">
              <span className="label">소속</span>
              <input
                type="text"
                className="table-input profile-input"
                placeholder="소속을 입력해 주세요"
                value={profileForm.department}
                onChange={(e) => handleProfileChange("department", e.target.value)}
                disabled={profileSaving}
              />
            </div>
            <div className="profile-item">
              <span className="label">담당업무</span>
              <input
                type="text"
                className="table-input profile-input"
                placeholder="담당업무를 입력해 주세요"
                value={profileForm.duty}
                onChange={(e) => handleProfileChange("duty", e.target.value)}
                disabled={profileSaving}
              />
            </div>
          </div>
          {(profileError || profileSuccess) && (
            <div className={profileError ? "error-text" : "info-text"}>
              {profileError || profileSuccess}
            </div>
          )}
          <div className="profile-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleProfileSave}
              disabled={profileSaving}
            >
              {profileSaving ? "저장 중..." : "인적 정보 저장"}
            </button>
          </div>
        </section>

        {/* 인재경영 비중 입력 */}
        <section className="panel">
          <h2 className="panel-title">주요 핵심 업무 활동 비중</h2>
          <p className="panel-help">
            귀하의 전체 업무 중 <strong>주요 핵심 업무 활동의 비중</strong>은
            전체 1년간의 업무 시간을 100으로 볼 때 얼마 정도입니까?
          </p>
          <div className="ratio-row">
            <input
              type="number"
              min="0"
              max="100"
              value={hraRatio}
              onChange={handleHraRatioChange}
              className="ratio-input"
              placeholder="예: 40"
              disabled={taskStatus === "submitted"}
            />
            <span className="ratio-symbol">%</span>
          </div>
          {hraRatioError && (
            <div className="error-text">{hraRatioError}</div>
          )}
          {!hraRatioError && hraRatio !== "" && (
            <div className="info-text">
              입력한 주요 핵심 업무 활동 비중: <strong>{hraRatio}%</strong>
            </div>
          )}
        </section>

        {/* 수행 업무 리스트 입력 */}
        <section className="panel">
          <div className="panel-header-row">
            <h2 className="panel-title">수행 업무 입력</h2>
            <button
              type="button"
              className="btn-secondary"
              onClick={addTaskRow}
              disabled={taskStatus === "submitted"}
            >
              + 업무 추가
            </button>
          </div>
          <p className="panel-help">
            주요 핵심 업무 활동을 포함하여,{" "}
            <strong>주요 수행 업무를 최대 {MAX_TASK_ROWS}개까지</strong> 입력해 주세요.
          </p>

          {jobTasksLoading && (
            <div className="info-text">업무분장표를 불러오는 중입니다...</div>
          )}
          {jobTasksError && <div className="error-text">{jobTasksError}</div>}

          <div className="task-table-wrapper">
            <table className="task-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>No.</th>
                  <th style={{ width: "140px" }}>분류</th>
                  <th>수행업무</th>
                  <th style={{ width: "120px" }}>수행주기</th>
                  <th style={{ width: "120px" }}>투입시간(시간)</th>
                  <th style={{ width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task, index) => (
                  <tr key={task.id}>
                    <td>{index + 1}</td>
                    <td>
                      <select
                        className="table-input"
                        value={task.category}
                        onChange={(e) =>
                          handleTaskChange(task.id, "category", e.target.value)
                        }
                        disabled={taskStatus === "submitted"}
                      >
                        <option value="">분류 선택</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {(() => {
                        const options = jobTasks.filter(
                          (jt) => jt.category === task.category
                        );
                        const disabled = !task.category || options.length === 0;

                        return (
                          <select
                            className="table-input"
                            value={task.taskName}
                            onChange={(e) =>
                              handleTaskChange(
                                task.id,
                                "taskName",
                                e.target.value
                              )
                            }
                            disabled={disabled || taskStatus === "submitted"}
                          >
                            <option value="">
                              {disabled
                                ? "먼저 분류를 선택해 주세요"
                                : "수행업무를 선택해 주세요"}
                            </option>
                            {options.map((jt) => (
                              <option key={jt.id} value={jt.taskName}>
                                {jt.taskName}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                    </td>
                    <td>
                      <select
                        className="table-input"
                        value={task.frequency}
                        onChange={(e) =>
                          handleTaskChange(
                            task.id,
                            "frequency",
                            e.target.value
                          )
                        }
                        disabled={taskStatus === "submitted"}
                      >
                        <option value="연간">연간</option>
                        <option value="반기">반기</option>
                        <option value="분기">분기</option>
                        <option value="월간">월간</option>
                        <option value="주간">주간</option>
                        <option value="일간">일간</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="table-input"
                        min="0"
                        placeholder="예: 40"
                        value={task.hours}
                        onChange={(e) =>
                          handleTaskChange(task.id, "hours", e.target.value)
                        }
                        disabled={taskStatus === "submitted"}
                      />
                    </td>
                    <td>
                      {tasks.length > 1 && taskStatus !== "submitted" && (
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => removeTaskRow(task.id)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 합계 / 수행비율 요약 */}
          <div className="summary-row">
            <div>
              <div className="summary-label">년간 투입 시간 총합</div>
              <div className="summary-value">
                {totalHours.toLocaleString()} 시간
              </div>
            </div>
            <div>
              <div className="summary-label">수행비율</div>
              <div
                className={
                  ratio > 100 ? "summary-value error-text" : "summary-value"
                }
              >
                {ratio}% {ratio > 100 && "(100% 초과)"}
              </div>
            </div>
          </div>
        </section>

        {/* 하단 버튼 */}
        <section className="bottom-actions">
          {saveError && (
            <div className="error-text" style={{ marginBottom: "8px" }}>
              {saveError}
            </div>
          )}
          {saveSuccess && !saveError && (
            <div className="info-text" style={{ marginBottom: "8px" }}>
              {saveSuccess}
            </div>
          )}
          {taskStatus === "submitted" && (
            <div className="info-text" style={{ marginBottom: "8px" }}>
              최종 제출 완료 상태입니다. 수정이 불가능합니다.
            </div>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={handleSubmit}
            disabled={saveLoading || taskStatus === "submitted"}
          >
            {saveLoading ? "저장 중..." : "저장"}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleFinalSubmit}
            disabled={saveLoading || taskStatus === "submitted"}
            style={{ marginLeft: "8px" }}
          >
            {saveLoading ? "제출 중..." : "최종제출"}
          </button>
        </section>
      </div>
    </div>
  );
}

export default MainPage;